import type { Icon } from '@phosphor-icons/react';
import { HourglassMediumIcon } from '@phosphor-icons/react';
import { type ComponentType, lazy, type ReactNode, Suspense } from 'react';
import { cn, EmptyState, Skeleton } from '../ui';

/** Contenitore con i margini laterali standard delle pagine. */
export function PageBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('px-4 lg:px-10', className)}>{children}</div>;
}

/** Segnaposto per le parti ancora in costruzione. */
export function ComingSoon({
  title,
  message,
  icon,
}: {
  title: string;
  message?: string;
  icon?: Icon;
}) {
  return (
    <EmptyState
      icon={icon ?? HourglassMediumIcon}
      title={title}
      message={message ?? 'Questa parte è in costruzione: arriva nei prossimi aggiornamenti.'}
    />
  );
}

export function PageSkeleton() {
  return (
    <div className="flex flex-col gap-3 px-4 py-6 lg:px-10" aria-busy="true">
      <Skeleton className="h-9 w-2/3" />
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="mt-4 h-40 w-full rounded-[var(--radius-sheet)]" />
    </div>
  );
}

/** Carica un componente solo quando serve (pagine dei moduli). */
export function lazyNamed<P extends object>(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
): ComponentType<P> {
  const Lazy = lazy(async () => {
    const mod = await loader();
    return { default: mod[name] as ComponentType<P> };
  });
  return function LazyNamed(props: P) {
    return (
      <Suspense fallback={<PageSkeleton />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
}
